import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { MainLayout } from '../components/MainLayout';
import JobFormModal from '../components/JobFormModal';
import {
  Plus, FileText, Filter, LayoutList, LayoutGrid, Search, X, Trash2, RotateCcw
} from 'lucide-react';
import { MultiSelectFilter } from '../components/MultiSelectFilter';
import { ContagensTable } from '../components/jobs/ContagensTable';
import { ContagensCardView } from '../components/jobs/ContagensCardView';
import { DatePicker } from '../components/DatePicker';
import { msg } from '../utils/messages';
import { useConfirm } from '../hooks/useConfirm';
import { useContagensPage, getDefaultDates } from '../hooks/useContagensPage';
import jobService from '../services/jobService';
// Story 2.60: aba "Excluídas" visível só pra MASTER
import useAuthStore from '../stores/authStore';
import PermissionGate from '../components/PermissionGate';

export const ContagensPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialog } = useConfirm();
  const [showModal, setShowModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState<any | null>(null);

  // Story 2.60.2: invalida o cache do calendario (React Query) apos mutar jobs aqui.
  // refetchType:'all' e necessario porque o calendario usa refetchOnMount:false —
  // sem isso o cache fica stale e a contagem excluida/restaurada continua aparecendo
  // ate limpar cache / navegar 2x.
  const invalidarCalendario = () =>
    queryClient.invalidateQueries({ queryKey: ['jobs', 'calendar'], refetchType: 'all' });

  const pageHook = useContagensPage();
  const {
    jobs,
    loading,
    error,
    successMessage,
    setSuccessMessage,
    searchTerm,
    setSearchTerm,
    selectedHospitals,
    setSelectedHospitals,
    selectedClientes,
    setSelectedClientes,
    selectedAuditores,
    setSelectedAuditores,
    selectedStatus,
    setSelectedStatus,
    showFilters,
    setShowFilters,
    allFiltersExpanded,
    setAllFiltersExpanded,
    dataInicio,
    setDataInicio,
    dataFim,
    setDataFim,
    viewMode,
    setViewMode,
    hospitalOptions,
    clienteOptions,
    auditorOptions,
    statusOptions,
    filteredJobs,
    sortedJobs,
    fetchJobs,
    clearAllFilters,
    // Story 2.60: lixeira
    activeTrashTab,
    setActiveTrashTab,
  } = pageHook;

  // Story 2.60: MASTER vê aba "Excluídas"
  const user = useAuthStore((state) => state.user);
  const isMaster = user?.role === 'MASTER';

  const handleOpenModal = (job: any | null = null) => {
    setSelectedJob(job);
    setShowModal(true);
  };

  const handleCloseModal = (shouldReload: boolean) => {
    setShowModal(false);
    setSelectedJob(null);
    if (shouldReload) {
      fetchJobs(dataInicio, dataFim);
      invalidarCalendario();
    }
  };

  const handleSuccess = (message: string) => setSuccessMessage(message);

  const handleNovaContagem = () => {
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0];
    navigate(`/cloud/contagens/new?date=${dateStr}`);
  };

  const handleDelete = async (id: number) => {
    const confirmed = await confirm({
      title: 'Deletar Contagem',
      message: 'Tem certeza que deseja deletar esta contagem?',
      variant: 'danger',
      confirmText: 'Deletar',
    });
    if (!confirmed) return;
    try {
      await jobService.deleteJob(id);
      setSuccessMessage('Contagem deletada com sucesso');
      fetchJobs(dataInicio, dataFim);
      invalidarCalendario();
    } catch (error: any) {
      console.error('Erro ao deletar contagem:', error);
      msg.error('Erro ao deletar contagem');
    }
  };

  // Story 2.60: restaurar contagem da lixeira (MASTER only)
  const handleRestore = async (id: number) => {
    const confirmed = await confirm({
      title: 'Restaurar Contagem',
      message: 'Restaurar esta contagem? Ela voltará para a aba Ativas.',
      variant: 'info',
      confirmText: 'Restaurar',
    });
    if (!confirmed) return;
    try {
      await jobService.restoreJob(id);
      setSuccessMessage('Contagem restaurada com sucesso');
      fetchJobs(dataInicio, dataFim, true); // ainda na aba Excluidas
      invalidarCalendario();
    } catch (error: any) {
      console.error('Erro ao restaurar contagem:', error);
      msg.error(error.response?.data?.detail || 'Erro ao restaurar contagem');
    }
  };

  const activeFiltersCount =
    selectedHospitals.length + selectedClientes.length + selectedAuditores.length + selectedStatus.length;

  if (loading) {
    return (
      <MainLayout className="dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Carregando contagens...</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <>
      <ConfirmDialog />
      <MainLayout>
        <div className="p-2">
          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center gap-3 animate-fade-in">
              <div className="text-green-600 dark:text-green-400">✅</div>
              <p className="text-sm font-medium text-green-800 dark:text-green-300">{successMessage}</p>
            </div>
          )}

          {/* Story 2.60: aba Ativas | Excluídas (MASTER only) */}
          {isMaster && (
            <div className="mb-4 flex items-center gap-2 border-b border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setActiveTrashTab('ativas')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTrashTab === 'ativas'
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
                title="Contagens ativas"
              >
                📋 Ativas
              </button>
              <button
                onClick={() => setActiveTrashTab('excluidas')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1 ${
                  activeTrashTab === 'excluidas'
                    ? 'border-red-600 text-red-600 dark:text-red-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
                title="Lixeira de contagens (MASTER only)"
              >
                <Trash2 className="w-4 h-4" />
                Excluídas
              </button>
            </div>
          )}

          {/* Action Bar */}
          <div className="mb-6 flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Histórico de Contagens</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">{filteredJobs.length} de {jobs.length} contagens</p>
            </div>
            <div className="flex gap-3">
              {/* Search */}
              <div className="relative hidden md:block w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por ID, hospital ou auditor..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* View Toggle */}
              <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('list')}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-colors ${
                    viewMode === 'list'
                      ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                  title="Visualização em lista"
                >
                  <LayoutList className="w-4 h-4" />
                  <span className="hidden sm:inline">Lista</span>
                </button>
                <button
                  onClick={() => setViewMode('card')}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-colors ${
                    viewMode === 'card'
                      ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                  title="Visualização em cards"
                >
                  <LayoutGrid className="w-4 h-4" />
                  <span className="hidden sm:inline">Cards</span>
                </button>
              </div>

              {/* Filters Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  showFilters || activeFiltersCount > 0
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <Filter className="w-4 h-4" />
                Filtros
                {activeFiltersCount > 0 && (
                  <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs">{activeFiltersCount}</span>
                )}
              </button>

              <PermissionGate tela="CONTAGENS_LIST" acao="criar">
                <button
                  onClick={handleNovaContagem}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Nova Contagem
                </button>
              </PermissionGate>
            </div>
          </div>

          {/* Period Filter (always visible) */}
          <div className="mb-4 flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Período:</span>
            <div className="w-40">
              <DatePicker value={dataInicio} onChange={setDataInicio} placeholder="Data início" />
            </div>
            <span className="text-sm text-gray-500">até</span>
            <div className="w-40">
              <DatePicker value={dataFim} onChange={setDataFim} placeholder="Data fim" />
            </div>
            <button
              onClick={() => { const d = getDefaultDates(); setDataInicio(d.inicio); setDataFim(d.fim); }}
              className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 px-2 py-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
            >
              ±15 dias
            </button>
            <button
              onClick={() => { setDataInicio(''); setDataFim(''); }}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 px-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Todos
            </button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex flex-col lg:flex-row lg:items-end gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 flex-1">
                  <MultiSelectFilter label="Clientes" options={clienteOptions} selectedValues={selectedClientes} onChange={setSelectedClientes} placeholder="Todos" expanded={allFiltersExpanded} showTodos showBadges />
                  <MultiSelectFilter label="Hospital" options={hospitalOptions} selectedValues={selectedHospitals} onChange={setSelectedHospitals} placeholder="Todos" expanded={allFiltersExpanded} showTodos showBadges />
                  <MultiSelectFilter label="Auditor" options={auditorOptions} selectedValues={selectedAuditores} onChange={setSelectedAuditores} placeholder="Todos" expanded={allFiltersExpanded} showTodos showBadges />
                  <MultiSelectFilter label="Status" options={statusOptions} selectedValues={selectedStatus} onChange={setSelectedStatus} placeholder="Todos" expanded={allFiltersExpanded} showTodos showBadges />
                </div>
                <div className="flex items-center gap-3 pb-0.5 shrink-0">
                  <button
                    onClick={() => setAllFiltersExpanded(!allFiltersExpanded)}
                    className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 px-3 py-2 rounded-lg transition-colors border border-transparent hover:border-blue-100 dark:hover:border-blue-800"
                  >
                    <span>{allFiltersExpanded ? '▼' : '▶'}</span>
                    <span className="whitespace-nowrap">{allFiltersExpanded ? 'Recolher' : 'Expandir'}</span>
                  </button>
                  <button
                    onClick={clearAllFilters}
                    className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 px-3 py-2 rounded-lg transition-colors border border-transparent hover:border-red-100 dark:hover:border-red-800"
                    title="Limpar todos os filtros"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span className="whitespace-nowrap">Limpar</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Content */}
          {error ? (
            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 flex flex-col items-center justify-center py-12 text-center">
              <div className="text-red-600 dark:text-red-400 mb-4">❌</div>
              <h3 className="text-xl font-bold text-red-900 dark:text-red-300 mb-2">Erro ao carregar contagens</h3>
              <p className="text-sm text-red-700 dark:text-red-400 mb-6">{error}</p>
              <button onClick={() => window.location.reload()} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors">
                Tentar novamente
              </button>
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 flex flex-col items-center justify-center py-12 text-center">
              <FileText className="w-16 h-16 text-gray-400 dark:text-gray-600 opacity-30 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {jobs.length === 0 ? 'Nenhuma contagem registrada' : 'Nenhuma contagem encontrada'}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                {jobs.length === 0
                  ? "Clique em 'Nova Contagem' para criar a primeira"
                  : 'Tente ajustar os filtros ou limpar a seleção'}
              </p>
              <PermissionGate tela="CONTAGENS_LIST" acao="criar">
                <button
                  onClick={handleNovaContagem}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors animate-pulse"
                >
                  <Plus className="w-4 h-4" />
                  Nova Contagem
                </button>
              </PermissionGate>
            </div>
          ) : viewMode === 'list' ? (
            <ContagensTable
              jobs={filteredJobs}
              onEdit={handleOpenModal}
              onDelete={handleDelete}
              // Story 2.60: passa onRestore quando aba Excluídas (renderiza botão Restaurar)
              onRestore={activeTrashTab === 'excluidas' ? handleRestore : undefined}
            />
          ) : (
            <ContagensCardView
              jobs={sortedJobs}
              onDelete={handleDelete}
              onRestore={activeTrashTab === 'excluidas' ? handleRestore : undefined}
            />
          )}
        </div>
      </MainLayout>

      {showModal && (
        <JobFormModal job={selectedJob} onClose={handleCloseModal} onSuccess={handleSuccess} />
      )}
    </>
  );
};
